import { isErrorCode, recoveryFor } from '@namu/protocol'
import type { ErrorCode, Page, Recovery } from '@namu/protocol'

/**
 * Talking to the Namu platform from the console.
 *
 * This is deliberately the mobile app's `api/client.ts` with one thing swapped
 * and the rest left recognisable, because the two products call the same server
 * and somebody will one day fix a bug in one of them and need to find the same
 * bug here. The swap is identity: the app registers a device once and swaps a
 * stored secret for a token, and a staff console cannot do that. A person types
 * an email and a password, gets an access token and a refresh token, and the
 * refresh token is the durable half.
 *
 * Four properties this has to have, and each one is a failure that only shows
 * up under load or on somebody else's afternoon.
 *
 * A refresh happens once, however many callers want one. A console opens six
 * panels at a time and every one of them fires a request on mount. Ten parallel
 * requests against a stale token must produce one call to the refresh endpoint,
 * not ten, because with refresh-token rotation the second through tenth are
 * replaying a token the first has already spent, and the server is right to
 * treat that as theft and end the session.
 *
 * A 401 is retried exactly once, and then the session is over. Retrying twice
 * is how a client loops against a server that has already decided, and not
 * retrying at all means every token expiry surfaces as a failed screen that
 * would have worked on reload.
 *
 * A refusal is told apart from a network failure. Refreshing while the wifi is
 * out must not sign somebody out of a form they have spent ten minutes filling
 * in; only an actual refusal from the server ends the session.
 *
 * Errors arrive typed. The server sends `{ error: { code, message, recovery } }`
 * from `namu-server/src/lib/errors.ts` with codes from `@namu/protocol`, so the
 * console switches on a code from the shared table rather than matching on the
 * words in a message.
 */

/* ===================== Errors ===================== */

/**
 * The wire shape, exactly as `errorBody()` writes it on the server.
 *
 * Typed loosely on purpose: this is what arrives, not what is promised. A
 * proxy, a load balancer or a crashed process can put anything at all here,
 * and the mapping below is what turns "anything at all" into something the
 * console can act on.
 */
export interface ApiErrorBody {
  error: {
    code?: unknown
    message?: unknown
    recovery?: unknown
    requestId?: unknown
  }
}

/**
 * Every failure the console can see, including the ones the server never sent.
 *
 * `offline` is not an `ErrorCode` because the server cannot produce it: the
 * request never arrived. Keeping it in the same union means one `switch` covers
 * both, and keeping it distinct from `internal` means the offline banner is
 * shown for the one case where it is true.
 */
export type ApiErrorCode = ErrorCode | 'offline'

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly recovery: Recovery
  readonly requestId: string | undefined
  /** From `retry-after` on a 429, in milliseconds. Undefined when absent. */
  readonly retryAfterMs: number | undefined

  constructor(init: {
    status: number
    code: ApiErrorCode
    message: string
    recovery: Recovery
    requestId?: string | undefined
    retryAfterMs?: number | undefined
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.recovery = init.recovery
    this.requestId = init.requestId
    this.retryAfterMs = init.retryAfterMs
  }

  /** True when there is no point trying the same thing again. */
  get fatal(): boolean {
    return this.recovery === 'fatal'
  }

  /** True when the way out is fresh credentials, not a retry. */
  get needsAuth(): boolean {
    return this.recovery === 'reauth'
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

/**
 * The code to assume when the body did not carry a usable one.
 *
 * This exists because the failures that skip the error handler are exactly the
 * ones with no JSON in them: an nginx 502 page, a 413 from a proxy, a 504 from
 * a load balancer that gave up before the server saw the request. Falling back
 * to `internal` for all of those would make every one of them retryable, and a
 * console that retries a 404 forever is a console that hangs on a deleted case.
 */
function codeForStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return 'bad_message'
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'bad_state'
    case 410:
      return 'session_expired'
    case 429:
      return 'rate_limited'
    case 503:
      return 'model_unavailable'
    default:
      return 'internal'
  }
}

function isRecovery(value: unknown): value is Recovery {
  return value === 'retry' || value === 'reauth' || value === 'fatal'
}

/**
 * Turns a failed response into an `ApiError`.
 *
 * The recovery hint is taken from the server when it sent a valid one and
 * derived from the code otherwise. That order matters both ways round. The
 * server is the authority because it may be running a newer code table than
 * this build knows about, and it is the half of the system that knows whether
 * an `internal` was the database blinking or the request being wrong. The local
 * derivation is what stops a proxy's HTML error page turning into an infinite
 * retry, because a body with no recovery in it would otherwise default to one.
 */
function errorFrom(status: number, parsed: unknown, headers: Headers): ApiError {
  const body = (parsed as ApiErrorBody | null)?.error
  const code: ErrorCode = isErrorCode(body?.code) ? body.code : codeForStatus(status)
  const message = typeof body?.message === 'string' ? body.message : 'Something went wrong.'
  const recovery = isRecovery(body?.recovery) ? body.recovery : recoveryFor(code)

  return new ApiError({
    status,
    code,
    message,
    recovery,
    requestId: typeof body?.requestId === 'string' ? body.requestId : undefined,
    retryAfterMs: retryAfterFrom(headers),
  })
}

/**
 * Reads `retry-after`, seconds form only.
 *
 * The HTTP-date form is ignored rather than parsed, because computing a wait
 * from it means subtracting the browser's clock from the server's, and a
 * machine whose clock is five minutes fast produces a negative delay that turns
 * a back-off into a hot loop against an endpoint that is already rate limiting.
 */
function retryAfterFrom(headers: Headers): number | undefined {
  const raw = headers.get('retry-after')
  if (raw === null) return undefined
  const seconds = Number(raw)
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : undefined
}

/* ===================== Requests ===================== */

export type QueryValue = string | number | boolean | readonly string[] | null | undefined

/** Query parameters. `undefined` and `null` are dropped, arrays repeat the key. */
export type Query = Record<string, QueryValue>

export interface RequestOptions {
  method?: string
  /** Serialised as JSON. Use `undefined`, not `null`, for no body. */
  body?: unknown
  query?: Query
  /**
   * Extra headers. Rare, and it exists for the one case that matters: sending
   * something alongside a GET that must not end up in a URL, and therefore must
   * not end up in every proxy access log between here and the server.
   */
  headers?: Record<string, string>
  /**
   * Defaults to true. Almost everything here lives under `/v1/org` and requires
   * a staff principal; the four `auth` endpoints that do not say so explicitly.
   */
  auth?: boolean
  signal?: AbortSignal
}

export interface CollectOptions {
  /**
   * The most rows to walk before giving up. A ceiling rather than an open
   * limit, because a filter that accidentally matches an entire audit log turns
   * an export into a tab that cannot be closed.
   */
  max?: number
}

/**
 * The result of walking a cursor to the end, or not.
 *
 * `complete` is not decoration. The whole argument for cursor pagination in
 * `@namu/protocol` is that a list which quietly omits rows is worse than one
 * that admits it stopped, and a helper that returned a bare array would put
 * that failure straight back in.
 */
export interface Collected<T> {
  items: T[]
  complete: boolean
}

export interface DownloadedFile {
  blob: Blob
  /** From `content-disposition`, when the server named it. */
  filename: string | undefined
}

/** What the server hands back for a successful login or refresh. */
export interface TokenGrant {
  accessToken: string
  refreshToken: string
  expiresInSeconds: number
}

export interface ApiClientOptions {
  baseUrl: string
  /** Injected by tests. Defaults to the global. */
  fetch?: typeof fetch
  now?: () => number
}

export interface ApiClient {
  readonly baseUrl: string
  request<T>(path: string, options?: RequestOptions): Promise<T>
  /** One page of a cursor-paginated list. */
  list<T>(path: string, query?: Query): Promise<Page<T>>
  /** Walks the cursor to the end, or to the ceiling. */
  collect<T>(path: string, query?: Query, options?: CollectOptions): Promise<Collected<T>>
  /** For exports, which must not go anywhere near `JSON.parse`. */
  download(path: string, options?: RequestOptions): Promise<DownloadedFile>
  /** Takes ownership of a fresh grant, after a login or an invitation. */
  adopt(grant: TokenGrant): void
  /** Drops both tokens. Does not notify; a deliberate sign-out is not a loss. */
  discard(): void
  /** Drops both tokens and asks the server to revoke the refresh token. */
  signOut(): Promise<void>
  hasSession(): boolean
  /** A valid access token, refreshing first if one is due. */
  accessToken(): Promise<string>
  /**
   * Fires when the session ends without the person asking: a refused refresh,
   * or a 401 that survived one. Returns its own unsubscribe.
   */
  onAuthLost(listener: () => void): () => void
}

/**
 * How long before expiry to renew.
 *
 * A minute covers a slow request and a clock a few seconds out of step with the
 * server's. Renewing exactly at expiry means the request that triggers the
 * renewal is racing it, and the one that loses shows an error for something
 * that was nobody's fault.
 */
const RENEW_BEFORE_MS = 60_000

/** The default ceiling for `collect`. Roughly a hundred pages at the default size. */
const DEFAULT_COLLECT_MAX = 5_000

interface Session {
  access: string
  refresh: string
  /** Milliseconds since the epoch. */
  expiresAt: number
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '')
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis)
  const now = options.now ?? Date.now

  /*
   * Both tokens live here, in a closure, and nowhere else.
   *
   * What that defends against: a token surviving a closed tab on a shared
   * machine in a field office, which is the realistic threat for a console
   * whose users are not always at their own desk; and the class of XSS that
   * reads `localStorage` and posts the contents somewhere, because there is
   * nothing at rest to read.
   *
   * What it does not defend against, and it is worth being blunt: XSS itself.
   * Script running on this page can call `request()` as the signed-in user for
   * as long as the page is open, and no client-side storage choice changes
   * that. It also does not defend against a compromised dependency, and it does
   * not defend against the person walking away from an unlocked screen.
   *
   * What it costs is a reload. Refreshing the page signs the person out,
   * because there is nothing to restore from. That is a real cost and it is
   * accepted rather than hidden: the fix is a refresh token in an httpOnly,
   * SameSite=Strict, Secure cookie set by the server, which survives a reload
   * without ever being readable by script. That is a server change and the
   * routes do not exist yet, so it is written down here rather than approximated
   * with `localStorage`, which would take the cost of the reload away and hand
   * back the exact exposure this avoids.
   */
  let session: Session | null = null

  /*
   * The single in-flight refresh. Every caller that arrives while one is
   * running waits on the same promise, which is what makes ten parallel
   * requests against a stale token produce one call and not ten. With refresh
   * tokens that rotate, the other nine would be replaying a spent token, and a
   * server that sees a spent refresh token is right to treat it as theft and
   * revoke the family.
   */
  let refreshing: Promise<string> | null = null

  const authLost = new Set<() => void>()

  function endSession(): void {
    // Nothing to lose means nothing to announce. Without this guard, ten
    // parallel requests failing together would fire ten sign-outs and the
    // login screen would be pushed onto the history stack ten times.
    if (session === null) return
    session = null
    refreshing = null
    for (const listener of authLost) listener()
  }

  async function refresh(stale: string): Promise<string> {
    const current = session
    if (current === null) throw notSignedIn()

    /*
     * Somebody else already replaced the token this caller was using, so hand
     * back the new one instead of spending the refresh token again. This is the
     * case where a request set off with a good token, sat behind a slow network
     * long enough for another request to refresh, and came back with a 401 that
     * has already been fixed. Without this check that request starts a second
     * refresh, and on a rotating server the token the rest of the app is using
     * is invalidated by it.
     */
    if (current.access !== stale) return current.access

    refreshing ??= (async () => {
      try {
        const grant = await request<TokenGrant>('/v1/org/auth/refresh', {
          method: 'POST',
          auth: false,
          body: { refreshToken: current.refresh },
        })
        session = grantToSession(grant, now())
        return grant.accessToken
      } catch (error) {
        /*
         * A refused refresh ends the session. A failed one does not.
         *
         * The distinction is the whole point: a lift, a dropped tunnel or a
         * server restart produces a `retry`, and treating that as a sign-out
         * throws away a half-written broadcast because the wifi blinked. Only
         * an answer that says these credentials are no good (expired, revoked,
         * unauthorized) clears them.
         */
        if (isApiError(error) && error.recovery === 'retry') throw error
        endSession()
        throw error
      } finally {
        refreshing = null
      }
    })()

    return refreshing
  }

  async function accessToken(): Promise<string> {
    const current = session
    if (current === null) throw notSignedIn()
    if (current.expiresAt - RENEW_BEFORE_MS > now()) return current.access
    return refresh(current.access)
  }

  /**
   * One attempt at a request, plus at most one retry after a refresh.
   *
   * `attempt` is a parameter rather than a loop counter so that the retry is
   * structurally incapable of happening twice: the recursive call passes 1, and
   * 1 does not qualify. A `while` with a flag reads the same and survives being
   * edited badly.
   */
  async function send(path: string, options: RequestOptions, attempt: 0 | 1): Promise<Response> {
    const useAuth = options.auth ?? true
    const headers = new Headers(options.headers)
    let presented: string | null = null

    if (options.body !== undefined) headers.set('content-type', 'application/json')
    if (useAuth) {
      presented = await accessToken()
      headers.set('authorization', `Bearer ${presented}`)
    }

    let response: Response
    try {
      response = await doFetch(`${baseUrl}${path}${queryString(options.query)}`, {
        method: options.method ?? 'GET',
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      })
    } catch (error) {
      /*
       * An aborted request is not a network failure and must not be dressed up
       * as one. The console cancels in-flight searches on every keystroke, and
       * reporting those as "could not reach Namu" would put an offline banner
       * on screen because somebody typed quickly.
       */
      if (options.signal?.aborted) throw error
      throw new ApiError({
        status: 0,
        code: 'offline',
        message: 'Could not reach Namu.',
        recovery: 'retry',
      })
    }

    if (response.ok) return response

    const text = await response.text()
    const error = errorFrom(response.status, text === '' ? null : safeParse(text), response.headers)

    if (response.status === 401 && useAuth && presented !== null) {
      if (attempt === 0) {
        // May itself throw, and that is the right shape: a refresh that is
        // refused ends the session and the original caller learns why.
        await refresh(presented)
        return send(path, options, 1)
      }
      // A fresh token was rejected too. Refreshing again cannot help: the
      // account is suspended, or the organization was deleted. Stop here rather
      // than discovering the same thing once per open panel.
      endSession()
    }

    throw error
  }

  async function request<T>(path: string, requestOptions: RequestOptions = {}): Promise<T> {
    const response = await send(path, requestOptions, 0)
    if (response.status === 204) return undefined as T
    const text = await response.text()
    return (text === '' ? undefined : safeParse(text)) as T
  }

  return {
    baseUrl,

    request,

    async list<T>(path: string, query?: Query) {
      return pageFrom<T>(await request<unknown>(path, { query }))
    },

    async collect<T>(path: string, query?: Query, collectOptions?: CollectOptions) {
      const max = collectOptions?.max ?? DEFAULT_COLLECT_MAX
      const items: T[] = []
      let cursor: string | undefined

      for (;;) {
        const page = pageFrom<T>(
          await request<unknown>(path, {
            query: { ...query, ...(cursor === undefined ? {} : { cursor }) },
          }),
        )
        items.push(...page.items)

        if (page.nextCursor === undefined) return { items, complete: true }
        if (items.length >= max) return { items, complete: false }
        /*
         * A cursor that does not move is a bug in the encoder, and the shape it
         * takes in a browser is a loop that fetches the same page until the tab
         * is killed. Stopping and saying the list is incomplete is the honest
         * failure; the alternative is a spinner nobody can escape.
         */
        if (page.nextCursor === cursor) return { items, complete: false }
        cursor = page.nextCursor
      }
    },

    async download(path, downloadOptions = {}) {
      const response = await send(path, downloadOptions, 0)
      return {
        blob: await response.blob(),
        filename: filenameFrom(response.headers.get('content-disposition')),
      }
    },

    adopt(grant) {
      session = grantToSession(grant, now())
    },

    discard() {
      session = null
      refreshing = null
    },

    async signOut() {
      /*
       * The local half happens first and unconditionally.
       *
       * A sign-out that waits for a round trip, and fails because the office
       * link is down, leaves somebody signed in on a machine they have already
       * walked away from, which is the situation the button exists for. The
       * server is told as a courtesy so the refresh token dies now rather than
       * at its expiry, and a failure to tell it changes nothing on this screen.
       */
      const current = session
      session = null
      refreshing = null
      if (current === null) return
      try {
        await request<void>('/v1/org/auth/logout', {
          method: 'POST',
          auth: false,
          body: { refreshToken: current.refresh },
        })
      } catch {
        // Deliberately swallowed. See above.
      }
    },

    hasSession() {
      return session !== null
    },

    accessToken,

    onAuthLost(listener) {
      authLost.add(listener)
      return () => authLost.delete(listener)
    },
  }
}

/**
 * Reads a cursor page, and refuses to invent one.
 *
 * `list` used to hand the parsed body back typed as `Page<T>` and checked as
 * nothing, which is a cast rather than a check: a 200 whose body is an
 * aggregate, or an error envelope a proxy rewrote, arrives as a `Page` whose
 * `items` is `undefined`. Twelve endpoint modules pass that straight to a
 * screen, and what a screen does with it is draw its empty state. So `/cases`
 * says "No cases have been opened", `/broadcasts` says nothing has been sent
 * and `/projects` says the organization has no projects, in the one situation
 * where the console has no idea whether any of that is true.
 *
 * `api/cases.ts` worked this out first, wrote a local `pageFrom` and left a note
 * saying the real fix belonged here because this file is shared. That note was
 * right, and this is it. The two failures are not equally bad and that is the
 * whole argument: a crash is loud and gets fixed, and a confident empty list is
 * read as a finding and acted on. On a platform whose product is "here is what
 * your community asked that nobody answered", a fabricated "nothing" is the
 * most expensive value it can print.
 *
 * `retry` and not `fatal`: a proxy, a load balancer or a half-finished deploy
 * is the realistic cause, and every screen already offers the retry that will
 * work once it passes.
 */
function pageFrom<T>(body: unknown): Page<T> {
  const envelope = body as { items?: unknown; nextCursor?: unknown } | null
  if (!Array.isArray(envelope?.items)) {
    throw new ApiError({
      status: 200,
      code: 'internal',
      message: 'Namu sent an answer this console could not read.',
      recovery: 'retry',
    })
  }
  return {
    items: envelope.items as T[],
    ...(typeof envelope.nextCursor === 'string' ? { nextCursor: envelope.nextCursor } : {}),
  }
}

function grantToSession(grant: TokenGrant, at: number): Session {
  return {
    access: grant.accessToken,
    refresh: grant.refreshToken,
    expiresAt: at + grant.expiresInSeconds * 1000,
  }
}

/**
 * Not an error the server sent, but the one it would send.
 *
 * Shaped as a real `unauthorized` so that a caller which asks for something
 * before signing in takes the same branch as one whose session ended, rather
 * than needing a second kind of check that somebody will forget.
 */
function notSignedIn(): ApiError {
  return new ApiError({
    status: 401,
    code: 'unauthorized',
    message: 'Not signed in.',
    recovery: 'reauth',
  })
}

/**
 * Builds a query string, dropping what has no value.
 *
 * `undefined` and `null` are dropped rather than serialised, because
 * `?status=undefined` is a filter the server has to either reject or ignore,
 * and every list screen here has optional filters that start empty.
 */
export function queryString(query: Query | undefined): string {
  if (query === undefined) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const entry of value) params.append(key, entry)
    } else {
      params.set(key, String(value))
    }
  }
  const encoded = params.toString()
  return encoded === '' ? '' : `?${encoded}`
}

/**
 * Builds a path with every interpolated value encoded.
 *
 * Used as ``path`/v1/org/cases/${id}` ``. It exists because case references and
 * enrolment codes are human strings rather than UUIDs: a reference containing a
 * slash silently addresses a different endpoint, and one containing `..` used
 * to be a way to address a different server entirely.
 */
export function path(strings: TemplateStringsArray, ...values: (string | number)[]): string {
  return strings.reduce((out, part, index) => {
    const value = values[index - 1]
    return value === undefined ? out + part : `${out}${encodeURIComponent(String(value))}${part}`
  })
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** Reads the filename out of `content-disposition`, quoted or bare. */
function filenameFrom(header: string | null): string | undefined {
  if (header === null) return undefined
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  const raw = match?.[1]
  if (raw === undefined) return undefined
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}
